/**
 * ScreenSizeService
 * 
 * Service responsible for detecting and managing screen size and device type information
 * throughout the MacSafety web application. Provides reactive signals and utility methods
 * for responsive UI design and device-specific functionality.
 * 
 * Key Features:
 * - Screen size detection (small, medium, large) based on width breakpoints
 * - Device type detection (phone, tablet, computer) using platform detection
 * - Reactive signals for real-time screen size and device type updates
 * - Window resize event handling and automatic updates
 * - Viewport height management for mobile browsers
 * - Mobile development tools (Eruda) initialization
 * - Convenience methods for responsive design logic
 * 
 * Breakpoint Definitions:
 * - Small: < 768px (mobile phones)
 * - Medium: 768px - 1024px (tablets)
 * - Large: > 1024px (desktop computers)
 * 
 * Components that utilize this service:
 * - All components requiring responsive design
 * - Form components for mobile vs desktop layouts
 * - Navigation components for mobile menu handling
 * - Modal and overlay components for sizing
 * - Any component needing device-specific behavior
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Injectable, signal } from '@angular/core';
import { Platform } from '@ionic/angular';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ScreenSizeService {
  // Private signal for tracking current screen size category
  private _screenSize = signal<'small' | 'medium' | 'large'>('large');
  
  // Private signal for tracking current device type
  private _deviceType = signal<'phone' | 'tablet' | 'computer'>('computer');
  
  constructor(private platform: Platform) {}

  /**
   * Public getter for screen size signal
   * @returns Readonly signal containing current screen size
   */
  get screenSize() {
    return this._screenSize;
  }

  /**
   * Public getter for device type signal
   * @returns Readonly signal containing current device type
   */
  get deviceType() {
    return this._deviceType;
  }

  /**
   * Determines screen size based on screen width breakpoints
   * Uses standard responsive design breakpoints for consistent UI behavior
   * 
   * Breakpoints:
   * - small: < 768px (mobile phones)
   * - medium: 768px - 1024px (tablets)
   * - large: > 1024px (desktop computers)
   * 
   * @returns Screen size category based on current window width
   */
  private determineScreenSizeByWidth(): 'small' | 'medium' | 'large' {
    const width = window.innerWidth;
    if (width < 768) {
      return 'small';
    } else if (width >= 768 && width < 1024) {
      return 'medium';
    } else {
      return 'large';
    }
  }

  /**
   * Determines device type based on platform detection and screen dimensions
   * Combines Ionic platform detection with screen size analysis for accurate device classification
   * 
   * @returns Device type category (phone, tablet, or computer)
   */
  private determineDeviceType(): 'phone' | 'tablet' | 'computer' {
    // Check if it's a mobile platform (iOS or Android)
    if (this.platform.is('ios') || this.platform.is('android')) {
      const width = this.platform.width();
      // Consider devices with width >= 768px as tablets, smaller as phones
      return width >= 768 ? 'tablet' : 'phone';
    }
    
    // If not mobile, it's a computer (desktop/laptop)
    return 'computer';
  }

  /**
   * Updates both screen size and device type based on current device and screen dimensions
   * Called during initialization and window resize events to keep signals current
   * Logs debug information for development and troubleshooting
   */
  public updateScreenSize(): void {
    const deviceType = this.determineDeviceType();
    const screenSize = this.determineScreenSizeByWidth();
    
    this._deviceType.set(deviceType);
    this._screenSize.set(screenSize);
    
    // Debug: Log screen size updates for development
    // console.log('ScreenSizeService: Screen size updated', {
    //   deviceType,
    //   screenSize,
    //   width: window.innerWidth,
    //   platform: {
    //     ios: this.platform.is('ios'),
    //     android: this.platform.is('android'),
    //     mobile: this.platform.is('ios') || this.platform.is('android')
    //   }
    // });
  }

  /**
   * Initializes the screen size service and sets up mobile detection
   * Called during application startup to establish initial screen size and device type
   * Sets up viewport height management and mobile development tools
   */
  public initialize(): void {
    // Update screen size and device type with current values
    this.updateScreenSize();
    this.setViewportHeight();

    // Initialize Eruda (mobile debugging tool) only on mobile devices in non-production mode
    const isMobile = this.platform.is('ios') || this.platform.is('android');
    if (isMobile && !environment.production) {
      const script = document.createElement('script');
      script.src = '//cdn.jsdelivr.net/npm/eruda';
      document.body.appendChild(script);
      script.onload = () => {
        (window as any).eruda.init();
      };
    }
  }

  /**
   * Sets up a listener for screen size changes and updates the state accordingly
   * Automatically detects window resize events and updates screen size signals
   * Ensures UI remains responsive to screen size changes
   */
  public setupScreenSizeListener(): void {
    window.addEventListener('resize', () => {
      // console.log('ScreenSizeService: Window resize detected', {
      //   width: window.innerWidth,
      //   height: window.innerHeight
      // });
      this.updateScreenSize();
    });
  }

  /**
   * Gets the current screen size category as a string
   * @returns Current screen size category ('small', 'medium', or 'large')
   */
  public getScreenSizeCategory(): 'small' | 'medium' | 'large' {
    return this._screenSize();
  }

  /**
   * Gets the current device type as a string
   * @returns Current device type category ('phone', 'tablet', or 'computer')
   */
  public getDeviceTypeCategory(): 'phone' | 'tablet' | 'computer' {
    return this._deviceType();
  }

  /**
   * Gets the current screen width in pixels
   * @returns Current window inner width
   */
  public getScreenWidth(): number {
    return window.innerWidth;
  }

  /**
   * Gets the current screen height in pixels
   * @returns Current window inner height
   */
  public getScreenHeight(): number {
    return window.innerHeight;
  }

  /**
   * Convenience method to check if current device is mobile (phone or tablet)
   * Useful for conditional logic that applies to all mobile devices
   * 
   * @returns True if device is phone or tablet, false if computer
   */
  public isMobileDevice(): boolean {
    const deviceType = this._deviceType();
    return deviceType === 'phone' || deviceType === 'tablet';
  }

  /**
   * Convenience method to check if current device is a phone
   * Useful for phone-specific UI adjustments and functionality
   * 
   * @returns True if device is a phone, false otherwise
   */
  public isPhone(): boolean {
    return this._deviceType() === 'phone';
  }

  /**
   * Convenience method to check if current device is a tablet
   * Useful for tablet-specific UI adjustments and functionality
   * 
   * @returns True if device is a tablet, false otherwise
   */
  public isTablet(): boolean {
    return this._deviceType() === 'tablet';
  }

  /**
   * Convenience method to check if current device is a computer
   * Useful for desktop-specific UI adjustments and functionality
   * 
   * @returns True if device is a computer, false otherwise
   */
  public isComputer(): boolean {
    return this._deviceType() === 'computer';
  }

  /**
   * Convenience method to check if screen size is small
   * Useful for responsive design logic targeting mobile devices
   * 
   * @returns True if screen size is small (< 768px), false otherwise
   */
  public isSmallScreen(): boolean {
    return this._screenSize() === 'small';
  }

  /**
   * Convenience method to check if screen size is medium
   * Useful for responsive design logic targeting tablet devices
   * 
   * @returns True if screen size is medium (768px - 1024px), false otherwise
   */
  public isMediumScreen(): boolean {
    return this._screenSize() === 'medium';
  }

  /**
   * Convenience method to check if screen size is large
   * Useful for responsive design logic targeting desktop devices
   * 
   * @returns True if screen size is large (> 1024px), false otherwise
   */
  public isLargeScreen(): boolean {
    return this._screenSize() === 'large';
  }

  /**
   * Sets up viewport height CSS custom property for mobile browsers
   * Addresses mobile browser viewport height issues (address bar, navigation bars)
   * Sets --vh CSS variable that can be used in CSS for viewport height calculations
   */
  private setViewportHeight(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }
} 